/**
 * SetterChipsInput Component
 *
 * Multi-setter selection component using chips for selected setters
 * and a searchable dropdown for adding new setters.
 */

import { useState, useRef, useEffect, type CSSProperties } from 'react';

interface Setter {
  id: string;
  name: string;
}

interface SetterChipsInputProps {
  /** List of all available setters to choose from */
  availableSetters: Setter[];
  /** Currently selected setters */
  selectedSetters: Setter[];
  /** Callback when selection changes */
  onChange: (setters: Setter[]) => void;
  /** Current user ID (pre-selected and cannot be removed) */
  currentUserId: string;
  /** Current user display name */
  currentUserName: string;
  /** Whether the input is disabled */
  disabled?: boolean;
}

/**
 * SetterChipsInput displays selected setters as chips with a search dropdown
 * for adding additional setters.
 *
 * @example
 * ```tsx
 * <SetterChipsInput
 *   availableSetters={gymSetters}
 *   selectedSetters={selectedSetters}
 *   onChange={setSelectedSetters}
 *   currentUserId={user.uid}
 *   currentUserName={user.displayName}
 * />
 * ```
 */
export function SetterChipsInput({
  availableSetters,
  selectedSetters,
  onChange,
  currentUserId,
  currentUserName: _currentUserName,
  disabled = false,
}: SetterChipsInputProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter available setters based on search and already selected
  const filteredSetters = availableSetters.filter(
    (setter) =>
      setter.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !selectedSetters.some((s) => s.id === setter.id)
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddSetter = (setter: Setter) => {
    onChange([...selectedSetters, setter]);
    setSearchQuery('');
    setIsDropdownOpen(false);
    inputRef.current?.focus();
  };

  const handleRemoveSetter = (setterId: string) => {
    // Don't allow removing the current user
    if (setterId === currentUserId) return;
    onChange(selectedSetters.filter((s) => s.id !== setterId));
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsDropdownOpen(true);
    }
  };

  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }
  };

  return (
    <div ref={containerRef} style={containerStyle}>
      {/* Selected setters as chips */}
      <div style={chipsContainerStyle}>
        {selectedSetters.map((setter) => {
          const isCurrentUser = setter.id === currentUserId;
          return (
            <div
              key={setter.id}
              style={{
                ...chipStyle,
                ...(isCurrentUser ? chipCurrentUserStyle : {}),
              }}
            >
              <span style={chipTextStyle}>{setter.name}</span>
              {!isCurrentUser && !disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveSetter(setter.id)}
                  style={removeButtonStyle}
                  aria-label={`Remove ${setter.name}`}
                >
                  ×
                </button>
              )}
              {isCurrentUser && <span style={chipYouStyle}>(you)</span>}
            </div>
          );
        })}
      </div>

      {/* Search input */}
      <div style={inputContainerStyle}>
        <input
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          placeholder={selectedSetters.length > 0 ? 'Add another setter...' : 'Search setters...'}
          style={inputStyle}
          disabled={disabled}
        />

        {/* Dropdown */}
        {isDropdownOpen && !disabled && (
          <div style={dropdownStyle}>
            {filteredSetters.length === 0 ? (
              <div style={emptyDropdownStyle}>
                {searchQuery ? 'No setters found' : 'All setters selected'}
              </div>
            ) : (
              filteredSetters.map((setter) => (
                <button
                  key={setter.id}
                  type="button"
                  onClick={() => handleAddSetter(setter)}
                  style={dropdownItemStyle}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {setter.name}
                </button>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== STYLES ====================

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

const chipsContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
};

const chipStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 12px',
  backgroundColor: 'var(--color-primary)',
  color: 'white',
  borderRadius: '16px',
  fontSize: '14px',
};

const chipCurrentUserStyle: CSSProperties = {
  backgroundColor: 'var(--color-primary)',
  opacity: 0.9,
};

const chipTextStyle: CSSProperties = {
  fontWeight: 500,
};

const chipYouStyle: CSSProperties = {
  fontSize: '12px',
  opacity: 0.8,
};

const removeButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'white',
  cursor: 'pointer',
  padding: '0',
  fontSize: '18px',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: '2px',
};

const inputContainerStyle: CSSProperties = {
  position: 'relative',
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  borderRadius: '8px',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const dropdownStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  backgroundColor: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border)',
  borderRadius: '8px',
  marginTop: '4px',
  maxHeight: '200px',
  overflowY: 'auto',
  zIndex: 10,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
};

const dropdownItemStyle: CSSProperties = {
  width: '100%',
  padding: '12px 16px',
  cursor: 'pointer',
  border: 'none',
  background: 'none',
  textAlign: 'left',
  fontSize: '14px',
  color: 'var(--color-text-primary)',
};

const emptyDropdownStyle: CSSProperties = {
  padding: '12px 16px',
  color: 'var(--color-text-secondary)',
  fontSize: '14px',
  textAlign: 'center',
};

export default SetterChipsInput;
